<template>
  <div
    class="col-span-6 sm:col-span-3 xl:col-span-2 flex flex-col justify-end items-center"
    v-if="loading"
  >
    <LoadingIcon icon="three-dots" class="w-8 h-8" />
  </div>
  <form class="validate-form" @submit.prevent="handleSave" v-if="!loading">
    <div>
      <div class="intro-y flex flex-col sm:flex-row items-center mt-8">
        <FileTextIcon />
        <h2 class="text-lg font-medium mr-auto">
          {{ $t('superadmin.tools.signUp.edit') }}
        </h2>
        <div class="w-full sm:w-auto flex mt-4 sm:mt-0">
          <button type="submit" class="btn btn-primary shadow-md flex items-center">
            <SaveIcon class="w-4 h-4 mr-2" /> {{ $t('common.save') }}
          </button>
          <router-link
            :to="{ name: 'superadmin-tools-sign-up-list' }"
            tag="a"
            class="btn shadow-md flex items-center"
          >
            <ArrowLeftCircleIcon class="w-4 h-4 mr-2" /> {{ $t('common.back') }}
          </router-link>
        </div>
      </div>
      <div class="pos intro-y grid grid-cols-12 gap-5 mt-5">
        <div class="col-span-12">
          <div class="intro-y box p-5">
            <div class="mt-3">
              <label for="name" class="form-label w-full flex flex-col sm:flex-row">
                {{ $t('common.personName') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.required') }},
                  {{ $t('form.constraint.gte2ch') }}
                </span>
              </label>
              <input
                id="name"
                v-model.trim="validate.name.$model"
                type="text"
                name="name"
                class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
                :class="{ 'border-theme-6': validate.name.$error }"
                placeholder="John"
              />
              <template v-if="validate.name.$error">
                <div
                  v-for="(error, index) in validate.name.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
            <div class="mt-3">
              <label for="lastname" class="form-label w-full flex flex-col sm:flex-row">
                {{ $t('common.personLastname') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.required') }},
                  {{ $t('form.constraint.gte2ch') }}
                </span>
              </label>
              <input
                id="lastname"
                v-model.trim="validate.lastname.$model"
                type="text"
                name="lastname"
                class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
                :class="{ 'border-theme-6': validate.lastname.$error }"
                placeholder="Doe"
              />
              <template v-if="validate.lastname.$error">
                <div
                  v-for="(error, index) in validate.lastname.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
            <div class="mt-3">
              <label for="email" class="form-label w-full flex flex-col sm:flex-row">
                Email
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.required') }}
                </span>
              </label>
              <input
                id="email"
                v-model.trim="validate.email.$model"
                type="email"
                name="email"
                class="intro-x login__input form-control py-3 px-4 border-gray-300 block mt-4"
                :class="{ 'border-theme-6': validate.email.$error }"
                placeholder="info@tatrykompas.sk"
                disabled="disabled"
              />
              <template v-if="validate.email.$error">
                <div
                  v-for="(error, index) in validate.email.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
            <div class="mt-3">
              <label
                for="phoneNumber"
                class="form-label w-full flex flex-col sm:flex-row"
              >
                {{ $t('common.phoneNumber') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.required') }}, {{ $t('form.constraint.phoneFormat') }}
                </span>
              </label>
              <input
                id="phoneNumber"
                v-model.trim="validate.phoneNumber.$model"
                type="text"
                name="phoneNumber"
                class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
                :class="{ 'border-theme-6': validate.phoneNumber.$error }"
                placeholder="+421xxxyyyzzz"
              />
              <template v-if="validate.phoneNumber.$error">
                <div
                  v-for="(error, index) in validate.phoneNumber.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
            <div class="mt-3">
              <label for="web" class="form-label w-full flex flex-col sm:flex-row">
                Web
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.optional') }}
                </span>
              </label>
              <input
                id="web"
                v-model.trim="validate.web.$model"
                type="text"
                name="web"
                class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
                :class="{ 'border-theme-6': validate.web.$error }"
                placeholder="https://www.tatryapp.com/"
              />
              <template v-if="validate.web.$error">
                <div
                  v-for="(error, index) in validate.web.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
            <div class="mt-3">
              <label for="iban" class="form-label w-full flex flex-col sm:flex-row">
                IBAN
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.optional') }}
                </span>
              </label>
              <input
                id="iban"
                v-model.trim="validate.iban.$model"
                type="text"
                name="iban"
                class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
                :class="{ 'border-theme-6': validate.iban.$error }"
                placeholder="SK00 0000 0000 0000 0000 0000"
              />
              <template v-if="validate.iban.$error">
                <div
                  v-for="(error, index) in validate.iban.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
            <div class="mt-3">
              <label
                for="businessName"
                class="form-label w-full flex flex-col sm:flex-row"
              >
                {{ $t('public.businessName') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.required') }}
                </span>
              </label>
              <input
                id="businessName"
                v-model.trim="validate.businessName.$model"
                type="text"
                name="businessName"
                class="intro-x login__input form-control py-3 px-4 border-gray-300 block mt-4"
                :class="{ 'border-theme-6': validate.businessName.$error }"
              />
              <template v-if="validate.businessName.$error">
                <div
                  v-for="(error, index) in validate.businessName.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
            <div class="mt-3">
              <label for="businessId" class="form-label w-full flex flex-col sm:flex-row">
                {{ $t('public.businessId') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.required') }}
                </span>
              </label>
              <input
                id="businessId"
                v-model.trim="validate.businessId.$model"
                type="text"
                name="businessId"
                class="intro-x login__input form-control py-3 px-4 border-gray-300 block mt-4"
                :class="{ 'border-theme-6': validate.businessId.$error }"
              />
              <template v-if="validate.businessId.$error">
                <div
                  v-for="(error, index) in validate.businessId.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
            <div class="mt-3">
              <label for="taxId" class="form-label w-full flex flex-col sm:flex-row">
                {{ $t('public.taxId') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.optional') }}
                </span>
              </label>
              <input
                id="taxId"
                v-model.trim="validate.taxId.$model"
                type="text"
                name="taxId"
                class="intro-x login__input form-control py-3 px-4 border-gray-300 block mt-4"
                :class="{ 'border-theme-6': validate.taxId.$error }"
              />
              <template v-if="validate.taxId.$error">
                <div
                  v-for="(error, index) in validate.taxId.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
            <div class="mt-3">
              <label
                for="vatRegistrationId"
                class="form-label w-full flex flex-col sm:flex-row"
              >
                {{ $t('public.vatRegistrationId') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.optional') }}
                </span>
              </label>
              <input
                id="vatRegistrationId"
                v-model.trim="validate.vatRegistrationId.$model"
                type="text"
                name="vatRegistrationId"
                class="intro-x login__input form-control py-3 px-4 border-gray-300 block mt-4"
                :class="{ 'border-theme-6': validate.vatRegistrationId.$error }"
              />
              <template v-if="validate.vatRegistrationId.$error">
                <div
                  v-for="(error, index) in validate.vatRegistrationId.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
            <div class="mt-3">
              <label for="street" class="form-label w-full flex flex-col sm:flex-row">
                {{ $t('address.street') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.required') }}
                </span>
              </label>
              <input
                id="street"
                v-model.trim="validate.street.$model"
                type="text"
                name="street"
                class="intro-x login__input form-control py-3 px-4 border-gray-300 block mt-4"
                :class="{ 'border-theme-6': validate.street.$error }"
              />
              <template v-if="validate.street.$error">
                <div
                  v-for="(error, index) in validate.street.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>

            <div class="mt-3">
              <label
                for="streetNumber"
                class="form-label w-full flex flex-col sm:flex-row"
              >
                {{ $t('address.streetNumber') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.required') }}
                </span>
              </label>
              <input
                id="streetNumber"
                v-model.trim="validate.streetNumber.$model"
                type="text"
                name="streetNumber"
                class="intro-x login__input form-control py-3 px-4 border-gray-300 block mt-4"
                :class="{ 'border-theme-6': validate.streetNumber.$error }"
              />
              <template v-if="validate.streetNumber.$error">
                <div
                  v-for="(error, index) in validate.streetNumber.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>

            <div class="mt-3">
              <label for="city" class="form-label w-full flex flex-col sm:flex-row">
                {{ $t('address.city') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.required') }}
                </span>
              </label>
              <input
                id="city"
                v-model.trim="validate.city.$model"
                type="text"
                name="city"
                class="intro-x login__input form-control py-3 px-4 border-gray-300 block mt-4"
                :class="{ 'border-theme-6': validate.city.$error }"
              />
              <template v-if="validate.city.$error">
                <div
                  v-for="(error, index) in validate.city.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>

            <div class="mt-3">
              <label for="zip" class="form-label w-full flex flex-col sm:flex-row">
                {{ $t('address.zip') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.required') }}, {{ $t('form.constraint.zipFormat') }}
                </span>
              </label>
              <input
                id="zip"
                v-model.trim="validate.zip.$model"
                type="text"
                name="zip"
                class="intro-x login__input form-control py-3 px-4 border-gray-300 block mt-4"
                :class="{ 'border-theme-6': validate.zip.$error }"
              />
              <template v-if="validate.zip.$error">
                <div
                  v-for="(error, index) in validate.zip.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
  <!-- BEGIN: Success Notification Content -->
  <div id="success-notification-content" class="toastify-content hidden flex">
    <CheckCircleIcon class="text-theme-9" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('superadmin.tools.signUp.edited') }}!</div>
      <!-- <div class="text-gray-600 mt-1">
          Please check your e-mail for further info!
        </div> -->
    </div>
  </div>
  <!-- END: Success Notification Content -->
  <!-- BEGIN: Failed Notification Content -->
  <div id="failed-notification-content" class="toastify-content hidden flex">
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('superadmin.tools.signUp.notEdited') }}!</div>
      <!-- <div class="text-gray-600 mt-1">Please check the fileld form.</div> -->
    </div>
  </div>
  <!-- END: Failed Notification Content -->
  <!-- BEGIN: Failed Notification Content -->
  <div id="failed-load-notification-content" class="toastify-content hidden flex">
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('superadmin.tools.signUp.notLoaded') }}!</div>
      <!-- <div class="text-gray-600 mt-1">Please check the fileld form.</div> -->
    </div>
  </div>
  <!-- END: Failed Notification Content -->
</template>

<script>
import { defineComponent, ref, toRefs, reactive } from 'vue'
import { required, helpers, email, minLength } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import Toastify from 'toastify-js'
import superadminApi from '@/services/superadmin/superadmin.api'

export default defineComponent({
  methods: {
    handleSave(data) {
      this.loading = true

      this.validate.$touch()
      if (this.validate.$invalid) {
        Toastify({
          node: cash('#failed-notification-content').clone().removeClass('hidden')[0],
          capacity: 10000,
          newWindow: true,
          close: true,
          gravity: 'top',
          position: 'right',
          stopOnFocus: true
        }).showToast()
        this.loading = false
        return
      }

      const saveRequest = {
        name: data.target.elements.name.value,
        lastname: data.target.elements.lastname.value,
        email: this.signUpData.email,
        phoneNumber: data.target.elements.phoneNumber.value,
        web: data.target.elements.web.value,
        iban: data.target.elements.iban.value,
        businessName: data.target.elements.businessName.value,
        businessId: data.target.elements.businessId.value,
        taxId: data.target.elements.taxId.value,
        vatRegistrationId: data.target.elements.vatRegistrationId.value,
        street: data.target.elements.street.value,
        streetNumber: data.target.elements.streetNumber.value,
        city: data.target.elements.city.value,
        zip: data.target.elements.zip.value,
        state: this.signUpData.state
      }
      superadminApi.editSignUpData(this.signUpData.uuid, saveRequest).then(
        (response) => {
          Toastify({
            node: cash('#success-notification-content').clone().removeClass('hidden')[0],
            capacity: 5000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.$router.push({ name: 'superadmin-tools-sign-up-list' })
        },
        (error) => {
          console.log(error)
          Toastify({
            node: cash('#failed-notification-content').clone().removeClass('hidden')[0],
            capacity: 10000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.loading = false
        }
      )
    }
  },
  computed: {
    console: () => console
  },
  data() {
    return {
      signUpData: {}
    }
  },
  mounted() {
    superadminApi.getSignUpDetail(this.$route.params.uuid).then(
      (response) => {
        this.signUpData = response.data

        this.formData.name = response.data.name
        this.formData.lastname = response.data.lastname
        this.formData.email = response.data.email
        this.formData.phoneNumber = response.data.phoneNumber
        this.formData.web = response.data.web
        this.formData.iban = response.data.iban
        this.formData.businessName = response.data.businessName
        this.formData.businessId = response.data.businessId
        this.formData.taxId = response.data.taxId
        this.formData.vatRegistrationId = response.data.vatRegistrationId
        this.formData.street = response.data.street
        this.formData.streetNumber = response.data.streetNumber
        this.formData.city = response.data.city
        this.formData.zip = response.data.zip
        this.formData.state = response.data.state

        this.loading = false
      },
      (error) => {
        console.log(error)
        Toastify({
          node: cash('#failed-load-notification-content')
            .clone()
            .removeClass('hidden')[0],
          duration: 10000,
          newWindow: true,
          close: true,
          gravity: 'top',
          position: 'right',
          stopOnFocus: true
        }).showToast()
        this.$router.push({ name: 'superadmin-tools-sign-up-list' })
      }
    )
  },
  setup() {
    const loading = ref(true)
    const phoneNumberValidator = helpers.regex(/^\+421[0-9]{9}$/)
    const zipValidator = helpers.regex(/^\s*\d\s*\d\s*\d\s*\d\s*\d\s*$/)
    const ibanValidator = helpers.regex(
      /^SK\d{2}\s?\d{4}\s?\d{4}\s?\d{2}[0-9A-Za-z]{2}\s[0-9A-Za-z]{4}\s[0-9A-Za-z]{4}?$/
    )
    const formData = reactive({
      name: '',
      lastname: '',
      email: '',
      phoneNumber: '',
      web: '',
      iban: '',
      businessName: '',
      businessId: '',
      taxId: '',
      vatRegistrationId: '',
      street: '',
      streetNumber: '',
      city: '',
      zip: '',
      state: 'SVK'
    })
    const rules = {
      name: {
        required: helpers.withMessage('form.validation.personName.required', required),
        minLength: helpers.withMessage('form.validation.gte2ch', minLength(2))
      },
      lastname: {
        required: helpers.withMessage(
          'form.validation.personLastname.required',
          required
        ),
        minLength: helpers.withMessage('form.validation.gte2ch', minLength(2))
      },
      email: {
        required: helpers.withMessage('form.validation.requiredField', required),
        email: helpers.withMessage('form.validation.email', email)
      },
      phoneNumber: {
        required: helpers.withMessage('form.validation.requiredField', required),
        phoneNumberValidator: helpers.withMessage(
          'form.validation.phoneNumberValidator',
          phoneNumberValidator
        )
      },
      web: {},
      iban: {
        ibanValidator: helpers.withMessage('form.validation.ibanValidator', ibanValidator)
      },
      businessName: {
        required: helpers.withMessage('form.validation.requiredField', required)
      },
      businessId: {
        required: helpers.withMessage('form.validation.requiredField', required)
      },
      taxId: {},
      vatRegistrationId: {},
      street: {
        required: helpers.withMessage('form.validation.requiredField', required)
      },
      streetNumber: {
        required: helpers.withMessage('form.validation.requiredField', required)
      },
      city: {
        required: helpers.withMessage('form.validation.requiredField', required)
      },
      zip: {
        required: helpers.withMessage('form.validation.requiredField', required),
        zipValidator: helpers.withMessage('form.validation.zipValidator', zipValidator)
      },
      state: {}
    }
    const validate = useVuelidate(rules, toRefs(formData))

    return {
      loading,
      validate,
      formData
    }
  }
})
</script>
